import React, { useState, ChangeEvent } from 'react';
import { transcribeAudio, translateText } from '../../services/api';
import { AxiosError } from 'axios';

interface AudioUploaderProps {
  onTranscriptionComplete: (text: string) => void;
  onTranslationComplete?: (text: string) => void;
  sourceLanguage: string;
  targetLanguage: string;
  context?: string;
  translationProvider?: string;
}

const AudioUploader: React.FC<AudioUploaderProps> = ({
  onTranscriptionComplete,
  onTranslationComplete,
  sourceLanguage,
  targetLanguage,
  context,
  translationProvider = 'dashscope',
}) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processingStep, setProcessingStep] = useState<string>('');

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select an audio file first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProcessingStep('Converting audio...');

    try {
      // Get file extension for format
      const format = selectedFile.name.split('.').pop()?.toLowerCase() || 'wav';

      // Step 1: Transcribe audio
      setProcessingStep('Converting speech to text...');
      const transcriptionResult = await transcribeAudio(selectedFile, sourceLanguage, format);

      if (!transcriptionResult || !transcriptionResult.text) {
        setError('No text received from audio');
        return;
      }

      // Call transcription callback
      onTranscriptionComplete(transcriptionResult.text);

      // Step 2: Automatically translate if translation callback is provided
      if (onTranslationComplete && transcriptionResult.text.trim()) {
        setProcessingStep('Translating text...');
        try {
          const translationResult = await translateText(
            transcriptionResult.text,
            sourceLanguage,
            targetLanguage,
            null, // model - use default
            translationProvider,
            context || null // context
          );
          
          if (translationResult && translationResult.translated_text) {
            onTranslationComplete(translationResult.translated_text);
          }
        } catch (translateErr) {
          console.error('Translation error:', translateErr);
          // Don't fail the whole process if translation fails
          setError('Conversion successful but translation failed. Please try again.');
        }
      }

      setProcessingStep('Completed!');
      // Reset file selection after successful upload
      setSelectedFile(null);
      
    } catch (err) {
      console.error('Transcription error:', err);
      const axiosError = err as AxiosError<{ detail?: string }>;
      setError(
        axiosError.response?.data?.detail ||
          axiosError.message ||
          'Unable to convert audio. Please try again.'
      );
      setProcessingStep('');
    } finally {
      setIsProcessing(false);
      setTimeout(() => setProcessingStep(''), 2000);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <input
          type="file"
          id="audio-file-input"
          accept="audio/*"
          onChange={handleFileChange}
          disabled={isProcessing}
          className="absolute w-0 h-0 opacity-0 overflow-hidden -z-10"
        />
        <label
          htmlFor="audio-file-input"
          className={`inline-block w-full px-6 py-3 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer text-center transition-all ${
            isProcessing 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-gray-50 hover:border-blue-500 hover:text-blue-600'
          } text-gray-600 text-sm`}
        >
          {selectedFile ? (
            <span className="font-medium">{selectedFile.name}</span>
          ) : (
            <span>📁 Select audio file to upload</span>
          )}
        </label>
      </div>

      {selectedFile && (
        <div className="p-3 bg-gray-50 rounded-md text-xs text-gray-600">
          <p className="mb-1"><strong>File:</strong> {selectedFile.name}</p>
          <p><strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!selectedFile || isProcessing}
        className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md text-sm font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none flex items-center justify-center gap-2"
      >
        {isProcessing ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{processingStep || 'Processing...'}</span>
          </>
        ) : (
          <span>🎤 Convert and Translate</span>
        )}
      </button>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-xs">
          {error}
        </div>
      )}
    </div>
  );
};

export default AudioUploader;



